import Autocomplete from "@mui/material/Autocomplete"
import Chip from "@mui/material/Chip"
import TextField from "@mui/material/TextField"
import { useState } from "react"

import { AUTOCOMPLETE_IN_DIALOG_PROPS } from "../autocompleteInDialogProps"
import { splitValues } from "../filterSchema"

const SEPARATOR = /[,;\n\r]/

/**
 * Typed values as chips. A comma, semicolon or new line starts a new chip, so a
 * pasted spreadsheet column becomes one chip per row, and the customer sees
 * exactly what will be searched before it is.
 */
export default function ChipsInput({
  label,
  placeholder,
  value,
  onChange,
  suggestions = [],
}) {
  const [inputValue, setInputValue] = useState("")
  const chips = Array.isArray(value) ? value : splitValues(value)

  function commit(extra) {
    const next = splitValues([...chips, extra])
    setInputValue("")
    if (next.length !== chips.length) {
      onChange(next)
    }
  }

  function handleInputChange(_event, text, reason) {
    if (reason === "reset") {
      return
    }
    if (SEPARATOR.test(text)) {
      // Everything before the last separator is finished; keep typing the rest.
      const parts = text.split(SEPARATOR)
      const pending = parts.pop()
      const next = splitValues([...chips, ...parts])
      if (next.length !== chips.length) {
        onChange(next)
      }
      setInputValue(pending)
      return
    }
    setInputValue(text)
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={suggestions}
      filterSelectedOptions
      value={chips}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(_event, next) => {
        setInputValue("")
        onChange(splitValues(next))
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={`${option}-${index}`}
            variant="filled"
            size="small"
            label={option}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={chips.length ? undefined : placeholder}
          onBlur={(event) => {
            params.inputProps?.onBlur?.(event)
            if (inputValue.trim()) {
              commit(inputValue)
            }
          }}
          onPaste={(event) => {
            const text = event.clipboardData?.getData("text") || ""
            if (!SEPARATOR.test(text)) {
              return
            }
            event.preventDefault()
            commit(`${inputValue}${text}`)
          }}
        />
      )}
      sx={{ width: "100%" }}
      {...AUTOCOMPLETE_IN_DIALOG_PROPS}
    />
  )
}
